import { useEffect, useRef, useState } from 'react'
import { searchGeniusArtists } from '../../api/pgpClient'

const inputClass =
  'w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none'

/**
 * Genius artist lookup for the PGP add-person flow.
 *
 * Picking a hit hands the caller `{ id, name, url }` to prefill the add form,
 * so the Genius id does not have to be typed by hand. When the backend has no
 * Genius token it replies 503 and this collapses to a hint — manual id entry
 * still works, so the add flow never depends on the search being available.
 */
export default function GeniusArtistSearch({ onPick }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [unavailable, setUnavailable] = useState(false)
  const [touched, setTouched] = useState(false)
  const reqRef = useRef(0)

  useEffect(() => {
    const q = query.trim()
    if (unavailable) return undefined
    if (q.length < 2) {
      setResults([])
      setError(null)
      return undefined
    }
    // Debounce: the box is typed into one character at a time.
    const handle = setTimeout(async () => {
      const seq = ++reqRef.current
      setBusy(true)
      setError(null)
      try {
        const data = await searchGeniusArtists({ q, limit: 8 })
        // Ignore a response that a newer keystroke has already superseded.
        if (seq !== reqRef.current) return
        setResults(Array.isArray(data?.results) ? data.results : [])
        setTouched(true)
      } catch (err) {
        if (seq !== reqRef.current) return
        const msg = err?.message || String(err)
        // 503 → no token configured; stop retrying on every keystroke.
        if (/not configured/i.test(msg)) {
          setUnavailable(true)
          setResults([])
        } else {
          setError(msg)
        }
      } finally {
        if (seq === reqRef.current) setBusy(false)
      }
    }, 350)
    return () => clearTimeout(handle)
  }, [query, unavailable])

  if (unavailable) {
    return (
      <p className="text-xs text-gray-500">
        Genius search is unavailable (no API token configured) — enter the Genius
        Artist ID below by hand.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">
        Search Genius
        <input
          type="text"
          className={inputClass}
          value={query}
          placeholder="artist name…"
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>

      {busy ? <p className="text-xs text-gray-500">Searching…</p> : null}
      {error ? <p className="text-xs text-rose-700">{error}</p> : null}
      {!busy && !error && touched && query.trim().length >= 2 && !results.length ? (
        <p className="text-xs text-gray-500">No Genius artists matched.</p>
      ) : null}

      {results.length ? (
        <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
          {results.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                onClick={() => {
                  onPick?.(row)
                  setQuery('')
                  setResults([])
                  setTouched(false)
                }}
              >
                <span className="min-w-0 flex-1 truncate font-medium text-gray-900">
                  {row.name}
                </span>
                <span className="shrink-0 tabular-nums text-xs text-gray-500">
                  {row.id}
                </span>
                {row.mapped ? (
                  <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                    Mapped
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
